'use client';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useReadContract, useWriteContract, useAccount, useBalance, useWaitForTransactionReceipt } from 'wagmi';
import { LRSNODE_CONTRACT_CONFIG, LRSNODE_V3_CONTRACT_CONFIG } from '@/configs/lrsnode.config';
import { parseEther, formatEther, parseUnits } from 'viem';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/sheet';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { RiLoader4Line, RiCheckLine, RiCloseLine } from '@remixicon/react';
import { publicClient } from '@/configs/client';

interface BuyNodeModalProps {
  isOpen: boolean;
  nodeKey: string | undefined;
  onClose: () => void;
  onSuccess: () => void;
  useV3?: boolean;
}

export default function BuyNodeModal({ isOpen, onClose, onSuccess, nodeKey, useV3 = false }: BuyNodeModalProps) {
  const PLAN_OPTIONS = [
    { id: 1, label: 'Pro key' },
    { id: 2, label: 'Lite key' },
  ] as const;

  const [referCode, setReferCode] = useState('');
  const [error, setError] = useState('');
  const [isPlanFull, setIsPlanFull] = useState(false);
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>(undefined);
  const { address: userAccount } = useAccount();
  const { data: balance } = useBalance({
    address: userAccount,
    query: {
      enabled: !!userAccount
    }
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [estimatedGas, setEstimatedGas] = useState<bigint | null>(null);
  const [gasPrice, setGasPrice] = useState<bigint | null>(null);

  // Memoize contract config to prevent infinite loops
  const contractConfig = useMemo(() => {
    return useV3 ? LRSNODE_V3_CONTRACT_CONFIG : LRSNODE_CONTRACT_CONFIG;
  }, [useV3]);

  const [selectedPlanId, setSelectedPlanId] = useState(1);

  const { data: requiredWeiForPlan } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE_V3_CONTRACT_CONFIG.abi,
    functionName: 'requiredWeiForPlan',
    args: [BigInt(selectedPlanId)],
    query: {
      enabled: useV3 && isOpen,
    }
  });

  const { data: selectedPlanData } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE_V3_CONTRACT_CONFIG.abi,
    functionName: 'plans',
    args: [BigInt(selectedPlanId)],
    query: {
      enabled: useV3 && isOpen,
    }
  });

  const { data: selectedPlanAvailable } = useReadContract({
    address: LRSNODE_V3_CONTRACT_CONFIG.address,
    abi: LRSNODE_V3_CONTRACT_CONFIG.abi,
    functionName: 'planAvailable',
    args: [BigInt(selectedPlanId)],
    query: {
      enabled: useV3 && isOpen,
    }
  });

  // Check if refer code exists
  const { data: referCodeOwner } = useReadContract({
    address: contractConfig.address,
    abi: contractConfig.abi,
    functionName: 'referCodeToAddress',
    args: [referCode],
    query: {
      enabled: referCode.length > 0,
    }
  });

  // Get gas price
  useEffect(() => {
    if (isOpen && userAccount) {
      const fetchGasPrice = async () => {
        try {
          const price = await publicClient.getGasPrice();
          setGasPrice(price);
        } catch (error) {
          console.error('Error fetching gas price:', error);
          // Use a default gas price if fetch fails (50 gwei)
          setGasPrice(parseUnits('50', 9));
        }
      };
      fetchGasPrice().then();
    }
  }, [isOpen, userAccount]);

  // Estimate gas for the transaction
  useEffect(() => {
    if (!isOpen || !userAccount || !balance) {
      setEstimatedGas(null);
      setIsPlanFull(false);
      return;
    }

    // For V3, wait for required plan quote to be loaded
    if (useV3 && !requiredWeiForPlan) {
      setEstimatedGas(null);
      return;
    }

    const estimateGasForTransaction = async () => {
      try {
        setIsPlanFull(false);
        let gasEstimate: bigint;
        const valueToSend = useV3 && requiredWeiForPlan ? requiredWeiForPlan as bigint : (balance.value - parseEther('0.1'));

        if (nodeKey && nodeKey !== '') {
          // Renew
          gasEstimate = await publicClient.estimateContractGas({
            account: userAccount,
            address: contractConfig.address,
            abi: contractConfig.abi,
            functionName: 'renewNodeKey',
            args: [nodeKey],
            value: valueToSend,
          });
        } else {
          // Buy
          if (referCode && referCode.length > 0 && referCodeOwner && referCodeOwner !== '0x0000000000000000000000000000000000000000') {
            gasEstimate = await publicClient.estimateContractGas({
              account: userAccount,
              address: contractConfig.address,
              abi: contractConfig.abi,
              functionName: 'buyNodeKeyWithRefer',
              args: [BigInt(selectedPlanId), referCode],
              value: valueToSend,
            });
          } else {
            gasEstimate = await publicClient.estimateContractGas({
              account: userAccount,
              address: contractConfig.address,
              abi: contractConfig.abi,
              functionName: 'buyNodeKeyWithOutRefer',
              args: [BigInt(selectedPlanId)],
              value: valueToSend,
            });
          }
        }
        setEstimatedGas(gasEstimate);
      } catch (error) {
        const message = error instanceof Error ? error.message.toLowerCase() : '';
        if (message.includes('full')) {
          setIsPlanFull(true);
          setEstimatedGas(null);
          return;
        }
        console.error('Error estimating gas:', error);
        setEstimatedGas(BigInt(240000));
      }
    };

    estimateGasForTransaction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userAccount, balance?.value, nodeKey, referCode, referCodeOwner, useV3, requiredWeiForPlan, selectedPlanId, contractConfig.address]);

  // Consolidated contract write function
  const { writeContractAsync, isPending: isWritePending } = useWriteContract();

  // Watch transaction status
  const { isLoading: isPending, isSuccess, isError } = useWaitForTransactionReceipt({
    hash: txHash,
    query: {
      enabled: !!txHash
    }
  });

  // Reset modal state when closed
  useEffect(() => {
    if (!isOpen) {
      setReferCode('');
      setSelectedPlanId(1);
      setError('');
      setIsPlanFull(false);
      setTxHash(undefined);
      setIsProcessing(false);
      setEstimatedGas(null);
      setGasPrice(null);
    }
  }, [isOpen]);

  // Calculate pricing details
  const pricingDetails = useMemo(() => {
    if (!balance || !gasPrice || !estimatedGas) return null;

    const nodePrice = useV3 && requiredWeiForPlan ? (requiredWeiForPlan as bigint) : (balance.value - parseEther('0.1'));
    const gasCost = estimatedGas * gasPrice;
    const totalCost = nodePrice + gasCost;

    return {
      nodePrice,
      gasCost,
      totalCost,
      hasEnoughBalance: balance.value >= totalCost,
    };
  }, [balance, gasPrice, estimatedGas, useV3, requiredWeiForPlan]);

  const handleBuy = async () => {
    try {
      setError('');
      setTxHash(undefined);
      setIsProcessing(true);

      // For V3, use oracle-derived required wei; for V2, use balance minus gas buffer
      let valueToSend: bigint;

      if (useV3 && requiredWeiForPlan) {
        valueToSend = requiredWeiForPlan as bigint;
      } else {
        // V2: Use balance minus gas buffer
        const gasBuffer = parseEther('5'); // 5 LRS for gas
        valueToSend = balance?.value ? balance.value - gasBuffer : BigInt(0);
      }

      // Check if user has enough balance
      if (pricingDetails && !pricingDetails.hasEnoughBalance) {
        setError(`Insufficient balance. Required: ${formatEther(pricingDetails.totalCost)} LRS`);
        setIsProcessing(false);
        return;
      }

      if (valueToSend <= BigInt(0)) {
        setError('Invalid amount to send');
        setIsProcessing(false);
        return;
      }

      let txHash: `0x${string}`;

      if(nodeKey && nodeKey !== ''){
        // Renew existing node
        txHash = await writeContractAsync({
          ...contractConfig,
          functionName: 'renewNodeKey',
          args: [nodeKey],
          value: valueToSend,
        } as any);
      } else {
        // Buy new node
        if (referCode && referCode.length > 0 && referCodeOwner && referCodeOwner !== '0x0000000000000000000000000000000000000000') {
          // Buy with refer code
          txHash = await writeContractAsync({
            ...contractConfig,
            functionName: 'buyNodeKeyWithRefer',
            args: [BigInt(selectedPlanId), referCode],
            value: valueToSend,
          } as any);
        } else {
          // Buy without refer code
          txHash = await writeContractAsync({
            ...contractConfig,
            functionName: 'buyNodeKeyWithOutRefer',
            args: [BigInt(selectedPlanId)],
            value: valueToSend,
          } as any);
        }
      }

      setTxHash(txHash);
    } catch (err: any) {
      console.error('Transaction error:', err);
      setError(err.message || 'Failed to process transaction');
      setIsProcessing(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow alphanumeric characters
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
    setReferCode(value);
  };

  const selectedPlan = useMemo(() => {
    if (!selectedPlanData) return undefined;
    if (Array.isArray(selectedPlanData)) {
      return {
        usdCents: selectedPlanData[0] as bigint,
        duration: selectedPlanData[1] as bigint,
        maxStock: selectedPlanData[2] as bigint,
        soldStock: selectedPlanData[3] as bigint,
        isActive: Boolean(selectedPlanData[4]),
      };
    }
    const plan = selectedPlanData as unknown as {
      usdCents: bigint;
      duration: bigint;
      maxStock: bigint;
      soldStock: bigint;
      isActive: boolean;
    };
    return plan;
  }, [selectedPlanData]);

  const formatUsdFromCents = (value?: bigint) => {
    if (value === undefined) return '$0.00';
    return `$${(Number(value) / 100).toFixed(2)}`;
  };

  const formatDuration = (seconds?: bigint) => {
    if (!seconds || seconds === 0n) return '-';
    const total = Number(seconds);
    if (total % 86400 === 0) return `${total / 86400} days`;
    if (total % 3600 === 0) return `${total / 3600} hours`;
    return `${Math.floor(total / 60)} minutes`;
  };

  const selectedPlanLabel =
    PLAN_OPTIONS.find((plan) => plan.id === selectedPlanId)?.label ?? `Plan #${selectedPlanId}`;

  // Keep latest callback without listing it in deps — inline parent handlers change every render
  // and would re-fire this effect while isSuccess stays true, causing a setState infinite loop.
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    if (!isSuccess) return;
    onSuccessRef.current();
  }, [isSuccess]);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="text-[#FF8A3C]">
            {nodeKey && nodeKey !== '' ? 'Renew Node' : 'Buy Node'}
          </SheetTitle>
          <SheetDescription>
            {nodeKey && nodeKey !== ''
              ? 'Renew your existing node to extend its validity period.'
              : 'Purchase a new LRSNode to start earning rewards.'}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 mx-4">
          {!txHash ? (
            <>
              {!(nodeKey && nodeKey !== '') && (
                <div>
                  {useV3 && (
                    <div className="mb-4">
                      <label className="text-sm font-medium mb-2 block">Plan</label>
                      <select
                        value={String(selectedPlanId)}
                        onChange={(e) => setSelectedPlanId(Number(e.target.value))}
                        disabled={isProcessing}
                        className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {PLAN_OPTIONS.map((plan) => (
                          <option key={plan.id} value={String(plan.id)}>
                            {plan.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Price is USD-based and converted to exact LRS by the contract.
                      </p>
                    </div>
                  )}
                  <label className="text-sm font-medium mb-2 block">
                    Enter refer code (optional)
                  </label>
                  <Input
                    type="text"
                    value={referCode}
                    onChange={handleInputChange}
                    placeholder="Refer code (optional)"
                    disabled={isProcessing}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {referCodeOwner && referCodeOwner !== '0x0000000000000000000000000000000000000000'
                      ? '✓ Valid refer code'
                      : referCode.length > 0
                      ? 'Invalid refer code'
                      : 'You can leave this blank'}
                  </p>
                </div>
              )}

              {/* Pricing Details */}
              <div className="bg-sidebar border border-border rounded-lg p-4 space-y-3">
                <h3 className="text-sm font-semibold text-[#FF8A3C] mb-3">Transaction Details</h3>
                {useV3 && selectedPlan && (
                  <div className="grid grid-cols-2 gap-2 text-xs bg-background/40 border border-border rounded-md p-3">
                    <div className="text-muted-foreground">Plan</div>
                    <div className="text-right font-medium">{selectedPlanLabel}</div>
                    <div className="text-muted-foreground">USD Price</div>
                    <div className="text-right font-medium">{formatUsdFromCents(selectedPlan.usdCents)}</div>
                    <div className="text-muted-foreground">Duration</div>
                    <div className="text-right font-medium">{formatDuration(selectedPlan.duration)}</div>
                    <div className="text-muted-foreground">Available</div>
                    <div className="text-right font-medium">{(selectedPlanAvailable as bigint | undefined)?.toString() ?? '0'}</div>
                  </div>
                )}
                {useV3 && isPlanFull && (
                  <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                    This plan is full. Choose another plan ID with available stock.
                  </div>
                )}
                {pricingDetails ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">{useV3 ? 'Plan Price (oracle)' : 'Node Price'}:</span>
                      <span className="font-medium">
                        {formatEther(pricingDetails.nodePrice)} LRS
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Estimated Gas Fee:</span>
                      <span className="font-medium">
                        {estimatedGas && gasPrice
                          ? `${formatEther(pricingDetails.gasCost)} LRS`
                          : (
                            <span className="text-muted-foreground flex items-center gap-1">
                              <RiLoader4Line className="size-3 animate-spin" />
                              Calculating...
                            </span>
                          )}
                      </span>
                    </div>
                    <div className="border-t border-border pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Total Cost:</span>
                        <span className="font-bold text-[#FF8A3C]">
                          {formatEther(pricingDetails.totalCost)} LRS
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-muted-foreground">Your Balance:</span>
                      <span className={pricingDetails.hasEnoughBalance ? 'text-green-400' : 'text-red-400'}>
                        {balance ? formatEther(balance.value) : '0'} LRS
                      </span>
                    </div>
                    {!pricingDetails.hasEnoughBalance && (
                      <div className="text-red-400 text-xs mt-1 bg-red-500/10 p-2 rounded">
                        Insufficient balance. You need {formatEther(pricingDetails.totalCost - (balance?.value || BigInt(0)))} more LRS
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <RiLoader4Line className="size-4 animate-spin" />
                    Loading pricing information...
                  </div>
                )}
              </div>

              {error && (
                <div className="text-red-500 text-sm bg-red-500/10 p-3 rounded-lg">{error}</div>
              )}

              <div className="flex space-x-3">
                <Button
                  onClick={handleBuy}
                  disabled={
                    isProcessing ||
                    isWritePending ||
                    !pricingDetails ||
                    (pricingDetails && !pricingDetails.hasEnoughBalance) ||
                    (useV3 && (!requiredWeiForPlan || !selectedPlan?.isActive || isPlanFull || (selectedPlanAvailable as bigint | undefined) === 0n)) ||
                    // Disable if invalid refer code is shown
                    (referCode.length > 0 && (!referCodeOwner || referCodeOwner === '0x0000000000000000000000000000000000000000'))
                  }
                  className="flex-1 bg-[#FF8A3C] hover:bg-[#FF8A3C]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing || isWritePending ? (
                    <div className="flex items-center justify-center space-x-2">
                      <RiLoader4Line className="size-4 animate-spin" />
                      <span>Processing...</span>
                    </div>
                  ) : (
                    `${(nodeKey && nodeKey !== '') ? 'Renew' : 'Buy'} Node`
                  )}
                </Button>
                <Button
                  onClick={onClose}
                  disabled={isProcessing || isWritePending}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <div className="py-8">
              {isPending && (
                <div className="flex flex-col items-center justify-center space-y-4">
                  <RiLoader4Line className="size-12 text-[#FF8A3C] animate-spin" />
                  <p className="text-center">Transaction pending... Please wait</p>
                </div>
              )}
              {isSuccess && (
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="size-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <RiCheckLine className="size-6 text-green-500" />
                  </div>
                  <p className="text-center text-green-500">Node {nodeKey ? 'renewed' : 'purchased'} successfully!</p>
                </div>
              )}
              {isError && (
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="size-12 rounded-full bg-red-500/20 flex items-center justify-center">
                    <RiCloseLine className="size-6 text-red-500" />
                  </div>
                  <p className="text-center text-red-500">Transaction failed. Please try again.</p>
                </div>
              )}

              <div className="mt-4 text-sm text-center">
                <a
                  href={`https://scan.larissa.network/tx/${txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FF8A3C] hover:text-[#FF8A3C]/80"
                >
                  View on Explorer →
                </a>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

